import { MAX_PAGINATION_BUTTONS } from '../constants/extraction';
import { SelectPopover } from './SelectPopover';
import './TablePagination.css';

interface TablePaginationProps {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  /**
   * Supply together to offer a page-size selector. Both are optional so the
   * tables that don't offer one render exactly as before.
   */
  pageSizeOptions?: readonly number[];
  onPageSizeChange?: (pageSize: number) => void;
}

export const TablePagination = ({
  currentPage,
  pageSize,
  totalCount,
  onPageChange,
  pageSizeOptions,
  onPageSizeChange,
}: TablePaginationProps) => {
  const totalPages = Math.ceil(totalCount / pageSize);
  const startEntry = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, totalCount);

  const showPageSize = !!onPageSizeChange && !!pageSizeOptions?.length;

  /*
    One page means there is nothing to page through — but if a selector is
    offered it has to stay reachable, because picking a larger size is exactly
    what collapses the list to one page. Returning null here would strip the
    control the moment it was used, leaving no way back to a smaller size.
  */
  if (totalPages <= 1 && !showPageSize) {
    return null;
  }

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = MAX_PAGINATION_BUTTONS;

    if (totalPages <= maxVisible) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page, last page, and pages around current
      pages.push(1);

      if (currentPage > 3) {
        pages.push('...');
      }

      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(totalPages - 1, currentPage + 1);
        i++
      ) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('...');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="table-pagination">
      <div className="table-pagination__info">
        Showing {startEntry} to {endEntry} of {totalCount} entries
      </div>
      {/*
        Page size and the pager share a right-hand group. With three flex children under
        space-between the size control landed dead centre, floating between the row count and the
        page buttons and belonging to neither.
      */}
      <div className="table-pagination__right">
        {showPageSize && (
          <div className="table-pagination__page-size">
            <label
              className="table-pagination__page-size-label"
              htmlFor="table-pagination-page-size"
            >
              Rows per page
            </label>
            {/*
              SelectPopover rather than a native select, so it is visibly a control and inherits the
              theme. `clearable={false}` because there is no unset page size — the placeholder is
              never a value this can hold.
            */}
            <SelectPopover
              id="table-pagination-page-size"
              options={pageSizeOptions?.map(String) ?? []}
              value={String(pageSize)}
              onChange={(next) => {
                const parsed = Number(next);
                if (Number.isFinite(parsed) && parsed > 0) onPageSizeChange?.(parsed);
              }}
              placeholder={String(pageSize)}
              clearable={false}
            />
          </div>
        )}
        {totalPages > 1 && (
        <div className="table-pagination__controls">
          <button
            className="table-pagination__button"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          {getPageNumbers().map((page, index) =>
            typeof page === 'number' ? (
              <button
                key={index}
                className={`table-pagination__button table-pagination__button--page ${
                  currentPage === page
                    ? 'table-pagination__button--active'
                    : ''
                }`}
                onClick={() => onPageChange(page)}
              >
                {page}
              </button>
            ) : (
              <span key={index} className="table-pagination__ellipsis">
                {page}
              </span>
            )
          )}

          <button
            className="table-pagination__button"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
        )}
        </div>
    </div>
  );
};
